import { useEffect, useRef, useState } from "react";
import { Utensils, Users } from "lucide-react";
import { http } from "@/lib/api";
import { useT } from "@/lib/i18n";

function useCountUp(target, duration = 1200) {
    const [val, setVal] = useState(0);
    const start = useRef(null);
    useEffect(() => {
        if (!target) { setVal(0); return; }
        let raf;
        const step = (ts) => {
            if (start.current == null) start.current = ts;
            const t = Math.min(1, (ts - start.current) / duration);
            const eased = 1 - Math.pow(1 - t, 3);
            setVal(Math.round(eased * target));
            if (t < 1) raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => raf && cancelAnimationFrame(raf);
    }, [target, duration]);
    return val;
}

export default function MealsCounter() {
    const { t } = useT();
    const [stats, setStats] = useState({ meals_donated: 0, customers_helped: 0 });
    useEffect(() => {
        http.get("/public/stats")
            .then(({ data }) => setStats(data))
            .catch(() => {});
    }, []);
    const meals = useCountUp(stats.meals_donated || 0);
    const customers = useCountUp(stats.customers_helped || 0);

    return (
        <section className="max-w-7xl mx-auto px-4 sm:px-6" data-testid="meals-counter">
            <div className="grid sm:grid-cols-2 gap-4">
                <div className="card-surface p-6 relative overflow-hidden">
                    <div className="absolute -right-6 -top-6 w-32 h-32 bg-emerald-500/10 blur-2xl rounded-full pointer-events-none" />
                    <div className="relative flex items-center gap-3">
                        <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-emerald-500/15 border border-emerald-500/35 text-emerald-300">
                            <Utensils className="w-5 h-5" />
                        </span>
                        <div>
                            <div className="text-xs mono uppercase tracking-widest text-slate-400">
                                {t("meals_label")}
                            </div>
                            <div className="text-3xl sm:text-4xl font-black text-white" data-testid="meals-number">
                                {meals.toLocaleString()}
                            </div>
                        </div>
                    </div>
                    <p className="relative mt-3 text-sm text-slate-400">
                        {t("meals_body")}
                    </p>
                </div>
                <div className="card-surface p-6 relative overflow-hidden">
                    <div className="absolute -right-6 -top-6 w-32 h-32 bg-emerald-500/10 blur-2xl rounded-full pointer-events-none" />
                    <div className="relative flex items-center gap-3">
                        <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-emerald-500/15 border border-emerald-500/35 text-emerald-300">
                            <Users className="w-5 h-5" />
                        </span>
                        <div>
                            <div className="text-xs mono uppercase tracking-widest text-slate-400">
                                {t("helped_label")}
                            </div>
                            <div className="text-3xl sm:text-4xl font-black text-white" data-testid="helped-number">
                                {customers.toLocaleString()}
                            </div>
                        </div>
                    </div>
                    <p className="relative mt-3 text-sm text-slate-400">
                        {t("helped_body")}
                    </p>
                </div>
            </div>
        </section>
    );
}
